import { useMemo } from "react";
import { useTheme } from "@mui/material";
import { toCurrency } from "@helpers";
import { BarChartItem } from "../types";

export const useChartOptions = (
  height: number,
  chartData: BarChartItem[] | undefined,
  color?: string,
  onBarClick?: (value: string) => void,
  selectedBar?: string,
  type: "cost" | "performance" = "cost"
): Highcharts.Options => {
  const theme = useTheme();

  const data = useMemo(() => {
    return chartData?.map((item) => ({
      ...item,
      id: item.key,
      color: item.color || color || theme.palette.success.main,
      selected: item.key === selectedBar,
    }));
  }, [chartData, color, theme.palette.success.main, selectedBar]);

  const isCostChart = type === "cost";
  const isPerformanceChart = type === "performance";

  return useMemo(() => {
    return {
      title: {
        text: "",
      },
      credits: {
        enabled: false,
      },
      chart: {
        type: "column",
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        height,
      },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.overline.fontSize as string,
            fontWeight: "500",
          },
        },
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
      },
      yAxis: {
        title: {
          text: "",
        },
        labels: {
          enabled: isPerformanceChart,
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.overline.fontSize as string,
            fontWeight: "500",
          },
        },
        gridLineColor: theme.palette.border.secondary,
      },
      tooltip: {
        enabled: isPerformanceChart,
      },
      plotOptions: {
        column: {
          allowPointSelect: false,
          states: {
            select: {
              color: theme.palette.graphVisual["01"].mid,
            },
          },
          point: {
            events: {
              click: (e: any) => onBarClick?.(e.point.key),
            },
          },
          cursor: "pointer",
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: {
            enabled: isCostChart,
            inside: false,
            align: "center",
            y: -1,
            style: {
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.overline.fontSize as string,
              fontWeight: "500",
              textOutline: "none",
            },
            formatter: function () {
              return toCurrency(this.y ?? 0, "EUR");
            },
          },
        },
      },
      series: [
        {
          colorByPoint: true,
          data: data,
          type: "column",
          name: "Value",
        },
      ],
      legend: {
        enabled: false,
      },
    };
  }, [height, data, theme, onBarClick, isCostChart, isPerformanceChart]);
};
